import { useRef, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, Loader2, Trash2, Upload } from 'lucide-react';
import { cn } from '@tes/ui';
import { api } from '../../lib/api';
import { messageOf } from '../../lib/errors';
import { MAX_DOCUMENTS } from '../../lib/quickSend';
import { useQuickSend } from './quickSendStore';

const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

/**
 * The documents in this send, in signing order. Each PDF uploads the moment
 * it's added so a bad file (encrypted, too big) fails here, not at Send, and
 * the canvas works against the server's own page sizes.
 */
export function DocumentList() {
  const docs = useQuickSend((s) => s.docs);
  const activeId = useQuickSend((s) => s.activeId);
  const openDoc = useQuickSend((s) => s.openDoc);
  const renameDoc = useQuickSend((s) => s.renameDoc);
  const moveDoc = useQuickSend((s) => s.moveDoc);
  const removeDoc = useQuickSend((s) => s.removeDoc);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(list: FileList | null) {
    const files = Array.from(list ?? []);
    const pdfs = files.filter(isPdf);
    const store = useQuickSend.getState();
    const added = store.addDocs(pdfs);
    const notes = [];
    if (files.length > pdfs.length) notes.push('Only PDFs can be added.');
    if (added.length < pdfs.length) notes.push(`A send holds up to ${MAX_DOCUMENTS} documents.`);
    setNotice(notes.join(' ') || null);
    for (const d of added) {
      api.createUpload(d.file).then(
        (res) => useQuickSend.getState().uploaded(d.id, res),
        (e) => useQuickSend.getState().uploadFailed(d.id, messageOf(e, 'Upload failed')),
      );
    }
  }

  const full = docs.length >= MAX_DOCUMENTS;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Documents</h2>
        <span className="text-xs text-slate-400">signed in this order</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          // Picking the same file again must still fire onChange.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={full}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!full) addFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex flex-col items-center gap-1 rounded-xl border-2 border-dashed px-3 py-5 text-sm text-slate-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          dragOver ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-slate-300 hover:border-brand-400 hover:text-brand-600',
        )}
      >
        <Upload size={20} />
        <span className="font-medium">{docs.length ? 'Add more PDFs' : 'Add PDFs'}</span>
        <span className="text-xs text-slate-400">click or drop here</span>
      </button>
      {notice && <p className="text-xs text-amber-700">{notice}</p>}

      <ol className="flex flex-col gap-2">
        {docs.map((d, i) => (
          <li
            key={d.id}
            onClick={() => openDoc(d.id)}
            className={cn(
              'cursor-pointer rounded-lg border bg-white px-3 py-2',
              d.id === activeId ? 'border-brand-400 ring-1 ring-brand-200' : 'border-slate-200 hover:border-slate-300',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                {i + 1}
              </span>
              <input
                value={d.title}
                onChange={(e) => renameDoc(d.id, e.target.value)}
                aria-label={`Title of document ${i + 1}`}
                className="min-w-0 flex-1 rounded border border-transparent px-1 py-0.5 text-sm font-medium hover:border-slate-200 focus:border-brand-400 focus:outline-none"
              />
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 pl-7">
              {d.state === 'uploading' ? (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Loader2 size={12} className="animate-spin" /> Uploading…
                </span>
              ) : d.state === 'failed' ? (
                <span className="flex items-center gap-1 text-xs text-red-600" title={d.error}>
                  <AlertCircle size={12} className="shrink-0" />
                  <span className="truncate">{d.error}</span>
                </span>
              ) : (
                <span className={cn('text-xs', d.fields.length ? 'text-slate-500' : 'text-amber-600')}>
                  {d.fields.length} field{d.fields.length === 1 ? '' : 's'} · {d.pageSizes.length} page
                  {d.pageSizes.length === 1 ? '' : 's'}
                </span>
              )}
              <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                <IconButton label="Move up" disabled={i === 0} onClick={() => moveDoc(d.id, -1)}>
                  <ArrowUp size={14} />
                </IconButton>
                <IconButton label="Move down" disabled={i === docs.length - 1} onClick={() => moveDoc(d.id, 1)}>
                  <ArrowDown size={14} />
                </IconButton>
                <IconButton label="Remove" danger onClick={() => removeDoc(d.id)}>
                  <Trash2 size={14} />
                </IconButton>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function IconButton({
  label,
  danger,
  disabled,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent',
        danger ? 'hover:text-red-600' : 'hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
